<template>
  <div class="dynamic-field-menu">
    <slot name="heading"></slot>
    <p v-if="max">{{currentValue.length}}/10 selected</p>
    <input v-model="q" type="text" placeholder="Search">
    <div v-if="showDynamicMessage">{{dynamicMessage}}</div>
    <scroll-area class="dynamic-box">
      <div v-if="shouldShow('userData')" class="collapse-item">
        <button
          data-toggle="collapse"
          href="#userDataCollapse"
          role="button"
          :disabled="!isPresent(hasUserDataMatch)"
          :aria-expanded="isOpen(hasUserDataMatch).toString()"
          aria-controls="submissionCollapse"
          :class="{disabled: !isPresent(hasUserDataMatch), collapsed: !isOpen(hasUserDataMatch)}">
          User Data
        </button>
        <div class="collapse" :class="{show: isOpen(hasUserDataMatch)}" id="userDataCollapse">
          <div v-for="section in userDataSections">
            <div v-if="section.header === 'Profiles'">
              <button
                data-toggle="collapse"
                :href="`#userData_${StringHelper.toDash(section.header)}`"
                :class="{disabled: !isPresent(section.sections), collapsed: !isOpen(section.sections)}"
                role="button"
                :disabled="!isPresent(section.sections)"
                :aria-expanded="isOpen(section.sections).toString()"
                :aria-controls="`${section.header}Collapse`">
                {{section.header}}
              </button>
              <div class="collapse" :class="{show: isOpen(section.sections)}"
                   :id="`userData_${StringHelper.toDash(section.header)}`">
                <div v-for="profile in section.sections">
                  <button
                    data-toggle="collapse"
                    :href="`#userData_${StringHelper.toDash(profile.header)}`"
                    :class="{disabled: !isPresent(profile.fields), collapsed: !isOpen(profile.fields)}"
                    role="button"
                    :disabled="!isPresent(profile.fields)"
                    :aria-expanded="isOpen(profile.fields).toString()"
                    :aria-controls="`${profile.header}Collapse`">
                    {{profile.header}}
                  </button>
                  <div class="collapse" :class="{show: isOpen(profile.fields)}"
                       :id="`userData_${StringHelper.toDash(profile.header)}`">
                    <button
                      v-for="field in profile.fields"
                      @click="select(profile.select(field, profile.type))"
                      type="button"
                      class="field-item">
                      {{field.label}}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div v-else>
              <button
                data-toggle="collapse"
                :href="`#userData_${StringHelper.toDash(section.header)}`"
                :class="{disabled: !isPresent(section.fields), collapsed: !isOpen(section.fields)}"
                role="button"
                :disabled="!isPresent(section.fields)"
                :aria-expanded="isOpen(section.fields).toString()"
                :aria-controls="`${section.header}Collapse`">
                {{section.header}}
              </button>
              <div class="collapse" :class="{show: isOpen(section.fields)}"
                   :id="`userData_${StringHelper.toDash(section.header)}`">
                <button
                  v-for="field in section.fields"
                  @click="select(section.select(field, section.type))"
                  type="button"
                  class="field-item">
                  {{field.label}}
                </button>
              </div>
            </div>

          </div>
        </div>
      </div>

      <template v-for="(handler, ind) in getAllHandlers()" v-if="handler.SECTIONS.includes('formFieldData')">
        <div v-if="handler.form" class="collapse-item">
          <button
            data-toggle="collapse"
            :href="`#submissionCollapse-formfield-${ind}`"
            role="button"
            :disabled="!isPresent(hasFieldDataMatch(handler))"
            :aria-expanded="isOpen(hasFieldDataMatch(handler)).toString()"
            aria-controls="submissionCollapse"
            :class="{disabled: !isPresent(hasFieldDataMatch(handler)), collapsed: !isOpen(hasFieldDataMatch(handler))}">
            {{handler.formFieldHeader}}
          </button>
          <div class="collapse" :class="{show: isOpen(hasFieldDataMatch(handler))}" :id="`submissionCollapse-formfield-${ind}`">
            <div v-for="section in formElementSections(handler)">
              <button
                data-toggle="collapse"
                :href="`#step_${section.workflowStep.client_id}`"
                :class="{disabled: !isPresent(section.formElements), collapsed: !isOpen(section.formElements)}"
                role="button"
                :disabled="!isPresent(section.formElements)"
                :aria-expanded="isOpen(section.formElements).toString()"
                :aria-controls="`${section.workflowStep.titleOrDefault}Collapse`">
                {{section.workflowStep.titleOrDefault}}
              </button>
              <div class="collapse" :class="{show: isOpen(section.formElements)}"
                   :id="`step_${section.workflowStep.client_id}`">
                <template v-if="modeIs('select')">
                  <div v-for="element in section.formElements.sort((a, b) => sortElements(a, b))">
                    <button
                      v-if="!inDocumentBuilder || (!element.is_repeatable && element.repeatableAncestors.length === 0)"
                      @click="select(section.select(element, section.workflowStep))"
                      type="button"
                      class="field-item">
                      {{element.referenceNameOrDefault}}
                    </button>
                    <div
                      v-else-if="(element.is_repeatable || element.repeatableAncestors.length === 1) && ![FormElementType.grid, FormElementType.stack].includes(element.element_type)"
                      class="collapse-item" :id="`element_${element.client_id}`">
                      <button
                        data-toggle="collapse"
                        :href="`#repeatable_collapse_${element.client_id}`"
                        role="button"
                        :aria-expanded="isOpen([element]).toString()"
                        :aria-controls="`${element.referenceNameOrDefault}Collapse`"
                        :class="`field-item ${{disabled: !isPresent([element]), collapsed: !isOpen([element])}}`">
                        {{element.referenceNameOrDefault}}
                      </button>
                      <div class="collapse" :class="{show: isOpen([element])}"
                           :id="`repeatable_collapse_${element.client_id}`">
                        <button
                          v-for="(_, index) in numberOfRepeatables(element)"
                          @click="select(section.select(element, section.workflowStep, index))"
                          type="button"
                          class="field-item">
                          {{element.referenceNameOrDefault}}.{{index + 1}}
                        </button>
                      </div>
                    </div>
                  </div>
                  <div v-for="element in section.dateOptions">
                    <button
                      @click="select(element.select(element))"
                      type="button"
                      class="field-item">
                      {{element.label}}
                    </button>
                  </div>
                </template>
                <template v-else-if="modeIs('checkbox')">
                  <div v-for="element in section.formElements" class="field-item">
                    <p v-tooltip="element.breadcrumbs.join(' > ') || null">
                      <input
                        v-model="currentValue"
                        type="checkbox"
                        :id="`dynamic_${element.client_id}`"
                        :value="element.client_id"
                        :disabled="!canEdit(element.client_id)">
                      <label :for="`dynamic_${element.client_id}`">{{element.referenceNameOrDefault}}</label>
                    </p>
                  </div>
                </template>
              </div>
            </div>
            <template v-if="shouldShow('submissionId')">
              <button
                v-for="section in fieldsHandler.submissionIdSections(lowerQ)"
                @click="select(section.select())"
                type="button"
                class="field-item">
                {{section.label}}
              </button>
            </template>
          </div>
        </div>
        <div v-else class="collapse-item disabled">No Linked Form</div>
      </template>

      <div v-if="shouldShow('awardedLicenseData')" class="collapse-item">
        <div v-if="fieldsHandler.license">
          <button
            data-toggle="collapse"
            href="#awardedLicensesData"
            role="button"
            :disabled="!isPresent(hasLicenseDataMatch)"
            :aria-expanded="isOpen(hasLicenseDataMatch).toString()"
            aria-controls="submissionCollapse"
            :class="{disabled: !isPresent(hasLicenseDataMatch), collapsed: !isOpen(hasLicenseDataMatch)}">
            License Data
          </button>
          <div class="collapse" :class="{show: isOpen(hasLicenseDataMatch)}" id="awardedLicensesData">
            <div v-for="section in licenseDataSections">
              <button
                data-toggle="collapse"
                :href="`#awarded_License_Data_${StringHelper.toDash(section.header)}`"
                :class="{disabled: !isPresent(section.fields), collapsed: !isOpen(section.fields)}"
                role="button"
                :disabled="!isPresent(section.fields)"
                :aria-expanded="isOpen(section.fields).toString()"
                :aria-controls="`${section.header}Collapse`">
                {{section.header}}
              </button>
              <div class="collapse" :class="{show: isOpen(section.fields)}"
                   :id="`awarded_License_Data_${StringHelper.toDash(section.header)}`">
                <button
                  v-for="field in section.fields"
                  @click="select(section.select(field, section.type))"
                  type="button"
                  class="field-item">
                  {{field.label}}
                </button>
              </div>
            </div>
          </div>

        </div>
        <div class="collapse-item disabled" v-else>No Linked License</div>
      </div>

      <div v-for="(handler, ind) in getAllHandlers()" v-if="handler.SECTIONS.includes('objectModelRecordMetadata')" class="collapse-item">
        <button
          data-toggle="collapse"
          :href="`#objectModelRecordMetadataCollapse-${ind}`"
          role="button"
          :aria-expanded="isOpen(handler.hasMetadataMatch(lowerQ)).toString()"
          aria-controls="objectModelRecordMetadataCollapse"
          :class="{collapsed: !isOpen(handler.hasMetadataMatch(lowerQ))}">
          {{        fieldsHandler.handlerConfig ? fieldsHandler.handlerConfig[ind].displayName : "About Record" }}
        </button>
        <div class="collapse" :class="{show: isOpen(handler.hasMetadataMatch(lowerQ))}" :id="`objectModelRecordMetadataCollapse-${ind}`">
          <button
            v-for="opt in handler.getOptions(lowerQ)"
            @click="select(handler.select(opt))"
            type="button"
            class="field-item">
            {{opt.name}}
          </button>
        </div>
      </div>

      <template v-for="(handler, ind) in getAllHandlers()" v-if="handler.SECTIONS.includes('objectModelPropertyData') && handler.objectModel">
        <div v-if="handler.mode === 'flat'" class="collapse-item">
          <button
            data-toggle="collapse"
            :href="`#objectModelPropertiesCollapse-${ind}`"
            role="button"
            :aria-expanded="isOpen(handler.hasObjectModelPropertyMatch(lowerQ)).toString()"
            aria-controls="objectModelPropertiesCollapse"
            :class="{collapsed: !isOpen(handler.hasObjectModelPropertyMatch(lowerQ))}">
            {{ fieldsHandler.handlerConfig ? fieldsHandler.handlerConfig[ind].displayName : "Object Model Properties"}}
          </button>
          <div class="collapse" :class="{show: isOpen(handler.
          hasObjectModelPropertyMatch(lowerQ))}" :id="`objectModelPropertiesCollapse-${ind}`">
            <template v-if="modeIs('select')">
              <button
                v-for="prop in handler.objectModelPropertiesSection(lowerQ).properties"
                @click="select(handler.objectModelPropertiesSection(lowerQ).select(prop))"
                type="button"
                class="field-item">
                {{prop.labelTitleDisplay}}
              </button>
            </template>
            <template v-else-if="modeIs('checkbox')">
              <div v-for="prop in handler.objectModelPropertiesSection(lowerQ).properties" class="field-item">
                <input
                  v-model="currentValue"
                  type="checkbox"
                  :id="`dynamic_${prop.client_id}`"
                  :disabled="!canEdit(prop.client_id)">
                <label :for="`dynamic_${prop.client_id}`">{{prop.labelTitleDisplay}}</label>
              </div>
            </template>
          </div>
        </div>

        <template v-else-if="handler.mode === 'type'">
          <div class="collapse-item">
            <button
              data-toggle="collapse"
              href="#objectModelPropertiesSortedCollapse"
              role="button"
              :disabled="!isPresent(handler.hasObjectModelPropertySortedMatch(lowerQ))"
              :aria-expanded="isOpen(handler.hasObjectModelPropertySortedMatch(lowerQ))"
              aria-controls="objectModelPropertiesSortedCollapse"
              :class="{collapsed: !isOpen(handler.hasObjectModelPropertySortedMatch(lowerQ))}">
              Object Model Properties
            </button>
            <div class="collapse" :class="{show: isOpen(handler.hasObjectModelPropertySortedMatch(lowerQ))}"
                 id="objectModelPropertiesSortedCollapse">
              <div v-for="section in handler.objectModelPropertySections(lowerQ)">
                <button
                  data-toggle="collapse"
                  :href="`#property-type-${section.propertyType}`"
                  :class="{collapsed: !isOpen(section.properties)}"
                  role="button"
                  :aria-expanded="isOpen(section.properties).toString()"
                  :aria-controls="`${section.propertyTypeName}Collapse`">
                  {{section.propertyTypeName}}
                </button>
                <div class="collapse" :class="{show: isOpen(section.properties)}"
                     :id="`property-type-${section.propertyType}`">
                  <button
                    v-for="property in section.properties"
                    @click="select(section.select(property))"
                    type="button"
                    class="field-item">
                    {{property.labelTitleDisplay}}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </template>
      </template>

      <div v-if="shouldShow('dates')" class="collapse-item">
        <button
          data-toggle="collapse"
          href="#dateTimeCollapse"
          role="button"
          :disabled="!isPresent(hasDateMatch)"
          :aria-expanded="isOpen(hasDateMatch).toString()"
          aria-controls="dateTimeCollapse"
          :class="{disabled: !isPresent(hasDateMatch), collapsed: !isOpen(hasDateMatch)}">
          Date and Time
        </button>
        <div class="collapse" :class="{show: isOpen(hasDateMatch)}" id="dateTimeCollapse">
          <div v-for="section in dateSections">
            <button
              @click="select(section.select(section))"
              type="button"
              class="field-item">
              {{section.label}}
            </button>
          </div>
        </div>
      </div>

      <div v-if="shouldShow('formulaBuilderFunctions')" class="collapse-item">
        <div v-for="(formulaFunctionSection, formulaFunctionTypeKey) in allFormulaFunctionsForDisplay"
             :key="formulaFunctionTypeKey">
          <button
            data-toggle="collapse"
            :href="`#${formulaFunctionTypeKey}FunctionTypeCollapse`"
            role="button"
            :aria-expanded="isOpen(hasFormulaMatch).toString()"
            :aria-controls="`${formulaFunctionTypeKey}FunctionTypeCollapse`"
            :class="{collapsed: !isOpen(hasFormulaMatch)}">
            {{formulaFunctionSection.displayName}} Functions
          </button>
          <div class="collapse" :id="`${formulaFunctionTypeKey}FunctionTypeCollapse`"
               :class="{show: isOpen(hasFormulaMatch)}">
            <button v-for="(formulaFunctionValue, formulaFunctionKey) in formulaFunctionSection.functions"
                    :key="formulaFunctionKey"
                    class="pill light-grey"
                    :id="`${formulaFunctionKey}`"
                    v-tooltip="formulaFunctionValue.description"
                    @click="select(formulaFunctionSection.select(formulaFunctionKey))">
              {{formulaFunctionKey}}(...)
            </button>
          </div>
        </div>
      </div>

      <template v-for="(handler, ind) in getAllHandlers()" v-if="handler.SECTIONS.includes('paymentInfo')">
        <div class="collapse-item">
          <button
            data-toggle="collapse"
            href="#paymentCollapse"
            role="button"
            aria-expanded="false"
            aria-controls="paymentCollapse">
            Payment Info
          </button>
          <div class="collapse" id="paymentCollapse">
            <div v-for="section in paymentDataSections">
              <button
                v-for="paymentData in section.paymentData"
                @click="select(section.select(paymentData, paymentData.id, section.workflowStep))"
                type="button"
                class="field-item">
                {{paymentData.label}}
              </button>
            </div>
          </div>
        </div>
      </template>

    </scroll-area>
    <div v-if="modeIs('checkbox')" class="cta">
      <button @click="cancel" class="btn block red outline">Cancel</button>
      <button @click="confirm" class="btn block green">Done</button>
    </div>
  </div>
</template>

<script lang="ts">
  import Vue from "vue";
  import WorkflowStep from "../../../formability/form_builder/models/workflow/workflowStep";
  import FormElement from "../../../formability/form_builder/models/formElements/formElement";
  import ObjectModelProperty from "../../models/objectModelProperties/objectModelProperty";
  import {IFormulaDefinition, IFormulaSelector} from "../../interfaces/formulaInterfaces";
  import {IDynamicReferencedThing, IUserDataSection} from "../../interfaces/dynamicFieldsInterfaces";
  import {ObjectModelPropertyRootType} from "../../enums/objectModelPropertyType";
  import {StringHelper} from "../../helpers/stringHelper";
  import {InstanceHelper} from "../../helpers/instanceHelper";
  import DynamicFieldsHandler from "../../models/dynamicFields/handlers/dynamicFieldsHandler";
  import {FormElementType} from "../../enums/formElementType";


  export default Vue.extend({
    props: {
      parent: {
        type: Object,
        default: null
      },
      restrictResultsToParentContext: {
        type: Boolean,
        default: false
      },
      mode: {
        type: String,
        default: 'select'
      },
      value: {
        default: null
      },
      max: { // used for checkbox mode
        type: Number,
        default: null
      },
      fieldsHandler: {
        type: Object as () => DynamicFieldsHandler,
      },
      inDocumentBuilder: {
        type: Boolean,
        default: false
      }
    },
    data: function () {
      return {
        q: '',
        StringHelper: StringHelper,
        hasSearched: false,
        currentValue: InstanceHelper.clone(this.value),
        showDynamicMessage: false,
        dynamicMessage: '',
        FormElementType
      };
    },
    computed: {
      licenseDataSections(): IUserDataSection[] {
        return this.fieldsHandler.licenseDataSections(this.lowerQ);
      },

      userDataSections(): IUserDataSection[] {
        return this.fieldsHandler.userDataSections(this.lowerQ);
      },
      hasUserDataMatch(): boolean {
        return this.fieldsHandler.hasUserDataMatch(this.lowerQ);
      },
      hasLicenseDataMatch(): boolean {
        return this.fieldsHandler.shouldShow('awardedLicenseData') && this.fieldsHandler.hasLicenseDataMatch(this.lowerQ);
      },
      dateSections(): IDynamicReferencedThing[] {
        return this.fieldsHandler.dateSections(this.lowerQ);
      },
      paymentDataSections(): IDynamicReferencedThing[] {
        return this.fieldsHandler.paymentDataSections(this.lowerQ);
      },
      hasDateMatch(): boolean {
        return this.fieldsHandler.hasDateMatch(this.lowerQ);
      },
      lowerQ(): string {
        return StringHelper.escape(this.q.toLowerCase());
      },
      maxReached(): boolean {
        if (this.mode === 'checkbox' && this.max !== null) {
          return this.currentValue.length >= this.max;
        } else {
          return false;
        }
      },
      hasFormulaMatch(): boolean {
        return this.fieldsHandler.hasFormulaMatch(this.lowerQ);
      },
      formulaFunctions(): IFormulaDefinition[] {
        return this.fieldsHandler.formulaFunctions(this.lowerQ);
      },
      allFormulaFunctionsForDisplay(): IFormulaSelector {
        return this.fieldsHandler.formulaSections(this.lowerQ);
      },
    },
    methods: {
      /**
       * config is shaped differently depending on scope wherein `select(config)` is called
       * make interface for `config` the same for all scopes that use method
       */
      select(config: any): void {
        this.$emit('select', config);
      },
      isPresent(item: Array<any> | boolean): boolean {
        return InstanceHelper.isPresent(item);
      },
      isOpen(item: Array<any> | boolean = null): boolean {
        if (!this.hasSearched) {
          return false;
        }
        return InstanceHelper.isPresent(item);
      },
      // FORM FIELD SECTION
      hasFormElementMatch(handler): boolean {
        return handler.hasFormElementMatch(this.lowerQ, this.parent, this.restrictResultsToParentContext);
      },
      hasFieldDataMatch(handler): boolean {
        return handler.hasFieldDataMatch(this.lowerQ, this.parent, this.restrictResultsToParentContext);
      },
      formElementSections(handler): { workflowStep: WorkflowStep, formElements: FormElement[] }[] {
        const sections = handler.formElementSections(this.lowerQ, this.parent, this.restrictResultsToParentContext);
        this.updateDynamicMessage(handler);
        return sections;
      },
      shouldShow(area: string): boolean {
        return this.fieldsHandler.shouldShow(area, this.lowerQ);
      },
      getAllHandlers(): DynamicFieldsHandler[] {
        return this.fieldsHandler.getAll();
      },
      modeIs(mode: string) {
        return this.mode === mode;
      },
      cancel(event): void {
        this.$emit('cancel', event);
      },
      confirm(): void {
        this.$emit('confirm', this.currentValue);
      },
      canEdit(item): boolean {
        if (this.mode === 'checkbox') {
          return !this.maxReached || this.currentValue.includes(item);
        } else {
          return true;
        }
      },
      updateDynamicMessage(handler = null): void {
        // must manually call this method - unsure why Vue is not being reactive to changes to these fields
        if (handler) {
          this.dynamicMessage = handler.dynamicMessage;
          this.showDynamicMessage = handler.showDynamicMessage;
        } else {
          this.dynamicMessage = this.fieldsHandler.dynamicMessage;
          this.showDynamicMessage = this.fieldsHandler.showDynamicMessage;
        }
      },
      numberOfRepeatables(el): Array<any> {
        if (el.max_repeatable_instances) {
          return Array(el.max_repeatable_instances);
        }

        const repeatableAncestor = el.repeatableAncestors[0];

        if (repeatableAncestor && repeatableAncestor.max_repeatable_instances) {
          return Array(repeatableAncestor.max_repeatable_instances);
        }

        return Array(20);
      },
      sortElements(a, _): number {
        if (a.id === null || a.is_repeatable || a.repeatableAncestors.length === 1) {
          return -1;
        }

        return 1;
      }
    },
    watch: {
      q: function (newVal, oldVal) {
        this.hasSearched = true;
      },
      currentValue: function (newVal, oldVal) {
        if (this.mode === 'checkbox') {
          this.$emit('input', newVal);
        }
      }
    }
  });
</script>
